import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { vehicleApi, uploadApi } from "@/services/api";
import { AppHeader } from "@/components/layout/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ArrowLeft,
  FileX,
  Loader2,
  Edit2,
  Save,
  X,
  Car,
  Fuel,
  Gauge,
  DollarSign,
  Settings,
  Users,
  Hash,
  Palette,
  ImagePlus,
  Trash2,
  Upload
} from "lucide-react";
import { toast } from "sonner";

const statusStyles = {
  available: "bg-success-light text-success",
  rented: "bg-primary/10 text-primary",
  maintenance: "bg-warning-light text-warning"
};

const categoryLabels = {
  sedan: "Sedan",
  suv: "SUV",
  truck: "Truck",
  luxury: "Luxury",
  electric: "Electric",
  van: "Van"
};

export default function VehiclePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    make: "",
    model: "",
    year: "",
    color: "",
    licensePlate: "",
    vin: "",
    category: "sedan",
    dailyRate: 0,
    status: "available",
    mileage: "",
    fuelType: "gasoline",
    transmission: "automatic",
    seats: 5,
    features: "",
    notes: "",
    imageUrl: ""
  });

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        setLoading(true);
        const data = await vehicleApi.getVehicle(id);
        setVehicle(data);
        setFormData({
          make: data.make || "",
          model: data.model || "",
          year: data.year || "",
          color: data.color || "",
          licensePlate: data.licensePlate || "",
          vin: data.vin || "",
          category: data.category || "sedan",
          dailyRate: data.dailyRate || 0,
          status: data.status || "available",
          mileage: data.mileage || "",
          fuelType: data.fuelType || "gasoline",
          transmission: data.transmission || "automatic",
          seats: data.seats || 5,
          features: data.features || "",
          notes: data.notes || "",
          imageUrl: data.imageUrl || ""
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching vehicle:', err);
        setError('Vehicle not found');
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading vehicle...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <FileX className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Vehicle Not Found</h1>
            <p className="text-muted-foreground mb-6">The vehicle you&apos;re looking for doesn&apos;t exist.</p>
            <Button onClick={() => navigate('/vehicles')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Vehicles
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const handleSave = async () => {
    try {
      await vehicleApi.updateVehicle(id, formData);
      setVehicle(prev => ({ ...prev, ...formData }));
      setIsEditing(false);
      toast.success("Vehicle Saved", {
        description: `${formData.year} ${formData.make} ${formData.model} has been updated.`
      });
    } catch (error) {
      toast.error("Failed to save vehicle");
    }
  };

  const handleCancel = () => {
    setFormData({
      make: vehicle.make || "",
      model: vehicle.model || "",
      year: vehicle.year || "",
      color: vehicle.color || "",
      licensePlate: vehicle.licensePlate || "",
      vin: vehicle.vin || "",
      category: vehicle.category || "sedan",
      dailyRate: vehicle.dailyRate || 0,
      status: vehicle.status || "available",
      mileage: vehicle.mileage || "",
      fuelType: vehicle.fuelType || "gasoline",
      transmission: vehicle.transmission || "automatic",
      seats: vehicle.seats || 5,
      features: vehicle.features || "",
      notes: vehicle.notes || "",
      imageUrl: vehicle.imageUrl || ""
    });
    setIsEditing(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type", {
        description: "Please upload a PNG, JPEG, WebP, or GIF image."
      });
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large", {
        description: "Maximum file size is 10MB."
      });
      return;
    }
    
    setUploadingImage(true);
    try {
      const result = await uploadApi.uploadVehicleImage(file);
      setFormData(prev => ({ ...prev, imageUrl: result.url }));
      toast.success("Image uploaded", {
        description: "Vehicle image has been uploaded successfully."
      });
    } catch (error) {
      toast.error("Failed to upload image", {
        description: error.response?.data?.detail || "Please try again."
      });
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, imageUrl: "" }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return uploadApi.getVehicleImageUrl(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation & Actions */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/vehicles')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Vehicles
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {isEditing ? (
                  <>
                    <Button onClick={handleSave} className="gap-2 bg-success hover:bg-success/90 text-success-foreground">
                      <Save className="w-4 h-4" />
                      Save Vehicle
                    </Button>
                    <Button variant="outline" onClick={handleCancel} className="gap-2">
                      <X className="w-4 h-4" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={() => setIsEditing(true)} className="gap-2">
                    <Edit2 className="w-4 h-4" />
                    Edit Vehicle
                  </Button>
                )}
            </div>
          </div>
        </div>

        {/* Vehicle Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-4">
                {/* Vehicle Image */}
                <div className="relative">
                  {formData.imageUrl ? (
                    <div className="relative w-32 h-24 rounded-xl overflow-hidden bg-muted">
                      <img 
                        src={getImageUrl(formData.imageUrl)} 
                        alt={`${formData.year} ${formData.make} ${formData.model}`}
                        className="w-full h-full object-cover"
                      />
                      {isEditing && (
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-1 right-1 p-1 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                          title="Remove image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-32 h-24 rounded-xl bg-primary/10">
                      <Car className="w-10 h-10 text-primary" />
                    </div>
                  )}
                  
                  {/* Upload button */}
                  {isEditing && (
                    <div className="mt-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="vehicle-image-upload"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-1 text-xs"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <ImagePlus className="w-3 h-3" />
                        )}
                        {formData.imageUrl ? 'Change' : 'Add Photo'}
                      </Button>
                    </div>
                  )}
                </div>
                
                <div>
                  {isEditing ? (
                    <div className="flex gap-2 mb-2">
                      <Input value={formData.year} onChange={(e) => updateField('year', e.target.value)} placeholder="Year" className="w-20 h-9" />
                      <Input value={formData.make} onChange={(e) => updateField('make', e.target.value)} placeholder="Make" className="w-28 h-9" />
                      <Input value={formData.model} onChange={(e) => updateField('model', e.target.value)} placeholder="Model" className="w-28 h-9" />
                    </div>
                  ) : (
                    <CardTitle className="text-2xl">
                      {formData.year} {formData.make} {formData.model}
                    </CardTitle>
                  )}
                  <CardDescription className="flex items-center gap-2 mt-1">
                    <span className="font-mono">{vehicle.id}</span>
                    {!isEditing && (
                      <Badge variant="outline" className={statusStyles[formData.status]}>
                        {formData.status?.toUpperCase()}
                      </Badge>
                    )}
                  </CardDescription>
                </div>
              </div>
              {isEditing && (
                <Select value={formData.status} onValueChange={(v) => updateField('status', v)}>
                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="rented">Rented</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Vehicle Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Car className="w-4 h-4 text-primary" />
                VEHICLE DETAILS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Color</Label>
                  {isEditing ? (
                    <Input value={formData.color} onChange={(e) => updateField('color', e.target.value)} className="h-9 mt-1" />
                  ) : (
                    <p className="text-sm font-medium flex items-center gap-2"><Palette className="w-3.5 h-3.5 text-muted-foreground" />{formData.color || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Category</Label>
                  {isEditing ? (
                    <Select value={formData.category} onValueChange={(v) => updateField('category', v)}>
                      <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                        <SelectItem value="truck">Truck</SelectItem>
                        <SelectItem value="luxury">Luxury</SelectItem>
                        <SelectItem value="electric">Electric</SelectItem>
                        <SelectItem value="van">Van</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium">{categoryLabels[formData.category] || formData.category}</p>
                  )}
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-xs text-muted-foreground">License Plate</Label>
                {isEditing ? (
                  <Input value={formData.licensePlate} onChange={(e) => updateField('licensePlate', e.target.value)} className="h-9 mt-1" />
                ) : (
                  <p className="text-sm font-medium font-mono flex items-center gap-2"><Hash className="w-3.5 h-3.5 text-muted-foreground" />{formData.licensePlate || '-'}</p>
                )}
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">VIN</Label>
                {isEditing ? (
                  <Input value={formData.vin} onChange={(e) => updateField('vin', e.target.value)} className="h-9 mt-1" />
                ) : (
                  <p className="text-sm font-medium font-mono">{formData.vin || '-'}</p>
                )}
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Mileage</Label>
                {isEditing ? (
                  <Input value={formData.mileage} onChange={(e) => updateField('mileage', e.target.value)} className="h-9 mt-1" placeholder="e.g., 15000" />
                ) : (
                  <p className="text-sm font-medium flex items-center gap-2"><Gauge className="w-3.5 h-3.5 text-muted-foreground" />{formData.mileage ? `${formData.mileage} km` : '-'}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Specifications */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Settings className="w-4 h-4 text-primary" />
                SPECIFICATIONS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Fuel Type</Label>
                  {isEditing ? (
                    <Select value={formData.fuelType} onValueChange={(v) => updateField('fuelType', v)}>
                      <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gasoline">Gasoline</SelectItem>
                        <SelectItem value="diesel">Diesel</SelectItem>
                        <SelectItem value="electric">Electric</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium flex items-center gap-2"><Fuel className="w-3.5 h-3.5 text-muted-foreground" />{formData.fuelType}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Transmission</Label>
                  {isEditing ? (
                    <Select value={formData.transmission} onValueChange={(v) => updateField('transmission', v)}>
                      <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="automatic">Automatic</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium">{formData.transmission}</p>
                  )}
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Seats</Label>
                {isEditing ? (
                  <Input type="number" value={formData.seats} onChange={(e) => updateField('seats', parseInt(e.target.value) || 5)} className="h-9 mt-1" />
                ) : (
                  <p className="text-sm font-medium flex items-center gap-2"><Users className="w-3.5 h-3.5 text-muted-foreground" />{formData.seats} passengers</p>
                )}
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-xs text-muted-foreground">Features</Label>
                {isEditing ? (
                  <Textarea value={formData.features} onChange={(e) => updateField('features', e.target.value)} className="mt-1" placeholder="Bluetooth, Backup Camera, etc." rows={2} />
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">{formData.features || 'No features listed'}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-primary" />
                PRICING
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Daily Rate</Label>
                  {isEditing ? (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-muted-foreground">$</span>
                      <Input type="number" value={formData.dailyRate} onChange={(e) => updateField('dailyRate', parseFloat(e.target.value) || 0)} className="h-9" />
                    </div>
                  ) : (
                    <p className="text-xl font-bold text-primary">{formatCurrency(formData.dailyRate)}<span className="text-sm font-normal text-muted-foreground">/day</span></p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold">NOTES</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <Textarea value={formData.notes} onChange={(e) => updateField('notes', e.target.value)} placeholder="Additional notes about this vehicle..." rows={4} />
              ) : (
                <p className="text-sm text-muted-foreground">{formData.notes || 'No additional notes.'}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
